"use client";

import type { ReactNode } from "react";
import { useState } from "react";

/**
 * A component that lazy-loads its children when they become visible in the viewport.
 * Utilizes Intersection Observer to detect visibility and conditionally renders the children
 * or a specified placeholder until the component is visible. Supports a testing mode to
 * always render the placeholder for testing purposes.
 *
 * @example
 * ```tsx
 * import dynamic from 'next/dynamic';
 *
 * const MyComponent = dynamic(() => import('../components/MyComponent'), {
 *   ssr: false, // Disable server-side rendering
 *   loading: () => <Placeholder />,
 * });
 *
 * // Default usage
 * <LazyLoadComponent>
 *   <MyComponent />
 * </LazyLoadComponent>
 *
 * // Custom placeholder
 * <LazyLoadComponent placeholder={<div>Custom Loading...</div>}>
 *   <MyComponent />
 * </LazyLoadComponent>
 *
 * // Testing mode
 * <LazyLoadComponent testingMode>
 *   <MyComponent />
 * </LazyLoadComponent>
 * ```
 *
 * @param props - The component props.
 * @param props.children - The React node(s) to be lazy-loaded when visible.
 * @param [props.placeholder] - Optional custom placeholder to display while loading. Defaults to the `Placeholder` component.
 * @param [props.testingMode] - Optional flag to always render the placeholder for testing purposes. Defaults to false.
 * @returns A React element that renders the children when visible or the placeholder otherwise.
 */

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "design-system/dialog";
import { Skeleton } from "design-system/skeleton";
import { Spinner } from "design-system/spinner";
import { useIntersectionObserver } from "usehooks-ts";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { cn } from "@/lib/utils";

type FormData = null | Record<string, unknown> | unknown;

type FormSubmitted = boolean;

type LazyLoadDialogChildrenProps = {
  formData: FormData;
  formSubmitted: FormSubmitted;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setFormSubmitted: React.Dispatch<React.SetStateAction<FormSubmitted>>;
};
export function EditNicknameLoadingState() {
  return (
    <div className="w-full space-y-6">
      {/* Address Nickname Section */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-10 w-full rounded-md" />
      </div>

      {/* Buttons Section */}
      <div className="flex justify-end">
        <Skeleton className="h-10 w-40 rounded-md" />
      </div>
    </div>
  );
}

function LazyLoadComponent({ children, placeholder }: { children: ReactNode; placeholder?: ReactNode }) {
  const effectivePlaceholder = placeholder ?? <Placeholder />;
  const { isIntersecting: isVisible, ref } = useIntersectionObserver({
    threshold: 0.1,
  });

  return (
    <div className="min-h-20" ref={ref}>
      {!isVisible ? effectivePlaceholder : children}
    </div>
  );
}

export function LazyLoadDialog<T = void>({
  children,
  description,
  descriptionClassname,
  descriptionOnSuccess,
  dialogControls,
  dialogWidthVariant = "md",
  noClickOutside,
  title,
  titleOnSuccess,
}: {
  children: ((props: LazyLoadDialogChildrenProps) => ReactNode) | ReactNode;
  description?: ReactNode;
  descriptionClassname?: string;
  descriptionOnSuccess?: ReactNode;
  dialogControls: UseDialogReturn<T>;
  dialogWidthVariant?: "md" | "sm";
  noClickOutside?: boolean;
  title: ReactNode;
  titleOnSuccess?: ReactNode;
}) {
  const [formSubmitted, setFormSubmitted] = useState<FormSubmitted>(false);
  const [formData, setFormData] = useState<FormData>(null);

  return (
    <Dialog {...dialogControls?.props}>
      <DialogContent
        className={cn(dialogWidthVariant === "sm" && "sm:max-w-lg", dialogWidthVariant === "md" && "sm:max-w-[800px]")}
        {...(noClickOutside ? { onPointerDownOutside: (event) => event.preventDefault() } : {})}
      >
        <DialogHeader>
          <DialogTitle>{formSubmitted ? titleOnSuccess || title : title}</DialogTitle>
          {description && (
            <DialogDescription className={descriptionClassname}>
              {formSubmitted ? descriptionOnSuccess || description : description}
            </DialogDescription>
          )}
        </DialogHeader>

        <LazyLoadComponent>
          {typeof children === "function"
            ? children({
                formData,
                formSubmitted,
                setFormData,
                setFormSubmitted,
              })
            : children}
        </LazyLoadComponent>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A placeholder component displayed while the lazy-loaded component is loading.
 * @returns A React element displaying a centered spinner.
 */
function Placeholder() {
  return (
    <div className="flex min-h-20 items-center justify-center">
      <Spinner className="size-8" />
    </div>
  );
}
